import MentorshipService, { MentorshipCampusOption, MentorshipCohortIdentityPayload } from './MentorshipService';

export interface MentorshipCohortFormValue extends MentorshipCohortIdentityPayload {
    campusIds: number[];
}

interface MentorshipCohortFormProps {
    value: MentorshipCohortFormValue;
    campuses: MentorshipCampusOption[];
    isGroup: boolean;
    isCreate: boolean;
    disabled?: boolean;
    onChange: (value: MentorshipCohortFormValue) => void;
}

const MentorshipCohortForm = ({
    value,
    campuses,
    isGroup,
    isCreate,
    disabled = false,
    onChange,
}: MentorshipCohortFormProps) => {
    const campusEditable = isCreate && isGroup && !disabled;
    const allCampusIds = campuses.map((campus) => campus.value);
    const allSelected = allCampusIds.length > 0 && allCampusIds.every((id) => value.campusIds.includes(id));

    const toggleCampus = (campusId: number) => {
        if (!campusEditable) return;
        const exists = value.campusIds.includes(campusId);
        onChange({
            ...value,
            campusIds: exists ? value.campusIds.filter((id) => id !== campusId) : [...value.campusIds, campusId],
        });
    };

    const toggleSelectAll = () => {
        if (!campusEditable) return;
        onChange({
            ...value,
            campusIds: allSelected ? [] : allCampusIds,
        });
    };

    return (
        <>
        <div className="card border-0 rounded-3 shadow-sm bg-white">
      <div className="card-header bg-white border-bottom py-3 px-4"><div className="card-title mb-0 font-16 font-700">Cohort Lifecycle</div></div>
      <div className="card-body p-4 d-flex flex-column gap-3">
        <div className="row g-3">
            <div className="col-md-6">
                <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor="mentorship-cohort-name">
                    Name
                </label>
                <input
                    id="mentorship-cohort-name"
                    type="text"
                    className="form-control font-13"
                    disabled={disabled}
                    value={value.name ?? ''}
                    onChange={(event) => onChange({ ...value, name: event.target.value })}
                />
            </div>
            {isGroup && (
                <div className="col-md-6">
                    <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor="mentorship-campus">
                        Choose Campus
                    </label>
                    {campusEditable && (
                        <div className="form-check mb-2">
                            <input
                                id="mentorship-campus-select-all"
                                type="checkbox"
                                className="form-check-input"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                            />
                            <label className="form-check-label" htmlFor="mentorship-campus-select-all">
                                Select All
                            </label>
                        </div>
                    )}
                    <div className="d-flex flex-wrap gap-3">
                        {campuses.map((campus) => {
                            const checked = value.campusIds.includes(campus.value);
                            return (
                                <div className="form-check" key={campus.value}>
                                    <input
                                        id={`mentorship-campus-${campus.value}`}
                                        type="checkbox"
                                        className="form-check-input"
                                        disabled={!campusEditable}
                                        checked={checked}
                                        onChange={() => toggleCampus(campus.value)}
                                    />
                                    <label className="form-check-label" htmlFor={`mentorship-campus-${campus.value}`}>
                                        {campus.label}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
          <div className="col-md-6">
            <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor="mentorship-cohort-start">
                    Start Date
                </label>
                <input
                    id="mentorship-cohort-start"
                    type="date"
                    className="form-control font-13"
                    disabled={disabled}
                    min={isCreate ? MentorshipService.todayCalendarDate() : undefined}
                    value={value.startDate ?? ''}
                    onChange={(event) => onChange({ ...value, startDate: event.target.value || null })}
                />
          </div>
          <div className="col-md-6">
            <label className="form-label font-11 font-700 text-uppercase tracking-wider text-muted mb-1" htmlFor="mentorship-cohort-end">
                    End Date
                </label>
                <input
                    id="mentorship-cohort-end"
                    type="date"
                    className="form-control font-13"
                    disabled={disabled}
                    min={
                        isCreate
                            ? value.startDate || MentorshipService.todayCalendarDate()
                            : undefined
                    }
                    value={value.endDate ?? ''}
                    onChange={(event) => onChange({ ...value, endDate: event.target.value || null })}
                />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center py-2 border-top">
          <div><label className="form-label font-13 font-700 text-uppercase tracking-wider text-muted mb-0">Enable cohort reshuffle at 6-month mark</label><div className="font-11 text-secondary">Admin can re-run matching and reassign mentees at mid-point</div></div>
          <div className="form-check form-switch fs-5 mb-0"><input
            id="mentorship-reshuffle-toggle"
            type="checkbox"
            className="form-check-input"
            role="switch"
            disabled={disabled}
            checked={value.reshuffleEnabled === true}
            onChange={(e) => onChange({ ...value, reshuffleEnabled: e.target.checked })}
        /></div>
        </div>
      </div>
    </div>
        
        </>
    );
};

export default MentorshipCohortForm;
